/* prettier-ignore-file */
import React, { useState, useEffect } from "react";
import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase.config";
import AuthHeader from "../components/AuthHeader";
import { useSettings, useAuthStore } from "../stores";
import { APP_VERSION, BUILD_TIMESTAMP, getVersionDisplay } from "../version";

const Dashboard = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [shopInfo, setShopInfo] = useState({
    address: "",
    phone: "",
    email: "",
  });
  const [storeLogo, setStoreLogo] = useState("");
  const [storeName, setStoreName] = useState("");

  // Format build timestamp to short format: "Mon, Jan 1 -- 3:45 PM"
  const formatBuildTimestamp = (timestamp) => {
    if (!timestamp || timestamp === "2025-10-21") {
      return timestamp;
    }
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return timestamp;
      
      const dayShort = date.toLocaleDateString("en-US", { weekday: "short" });
      const monthShort = date.toLocaleDateString("en-US", { month: "short" });
      const day = date.getDate();
      const time = date.toLocaleTimeString("en-US", { 
        hour: "numeric", 
        minute: "2-digit",
        hour12: true 
      });
      
      return `${dayShort}, ${monthShort} ${day} -- ${time}`;
    } catch (e) {
      return timestamp;
    }
  };

  // Check authentication - handled by store

  // Get settings from Zustand store
  const settings = useSettings((state) => state.settings);
  
  // Update state when settings change
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      const address = settings.startingAddress?.address || "";
      const phone = settings.storePhone?.phone || "";
      const email = settings.storeEmail?.email || "";
      const logo = settings.storeLogo?.url || "";
      const storeDisplayName = settings.storeName?.name || "";
      
      setShopInfo({ address, phone, email });
      setStoreLogo(logo);
      setStoreName(storeDisplayName);
    }
  }, [settings]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const menuItems = [
    {
      id: "calendar",
      label: "Calendar",
      icon: "📅",
      color: "#0E7C6B",
      route: "/calendar",
    },
    {
      id: "base",
      label: "Today",
      icon: "🏠",
      color: "#1F8A5B",
      route: "/today",
    },
    {
      id: "rental-bookings",
      label: "New Booking",
      icon: "🚴",
      color: "#B4761B",
      route: "/booking-wizard",
    },
    {
      id: "admin",
      label: "Bookings",
      icon: "⚙️",
      color: "#C4453D",
      route: "/bookings",
    },
  ];

  return (
    <View style={styles.container}>
      <AuthHeader
        title="Dashboard"
        subtitle={storeName ? `by ${storeName}` : ""}
        isDesktop={isDesktop}
        isAuthenticated={isAuthenticated}
        shopInfo={shopInfo}
        storeLogo={storeLogo}
      />
      
      <View style={[
        styles.content, 
        { maxWidth: isDesktop ? 600 : "100%" },
        !isDesktop && styles.contentMobile
      ]}>
        {/* Build Info Banner */}
        <View style={styles.buildInfoBanner}>
          <Text style={styles.buildInfoLabel}>Last Deploy:</Text>
          <Text style={styles.buildInfoValue}>{formatBuildTimestamp(BUILD_TIMESTAMP)}</Text>
          <Text style={styles.buildInfoVersion}>{getVersionDisplay()}</Text>
        </View>

        {isDesktop ? (
          <View style={styles.menuGridDesktop}>
            {menuItems.map((item) => (
              <TouchableOpacity dataSet={{ hover: "lift" }}
                key={item.id}
                style={[
                  styles.menuButton, 
                  { backgroundColor: item.color },
                  styles.menuButtonDesktop
                ]}
                onPress={() => navigate(item.route)}
                activeOpacity={0.8}
              >
                <Text style={styles.menuIconDesktop}>
                  {item.icon}
                </Text>
                <Text style={styles.menuLabelDesktop}>
                  {item.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        ) : (
          <>
            {menuItems.map((item) => (
              <TouchableOpacity dataSet={{ hover: "lift" }}
                key={item.id}
                style={[
                  styles.menuButton, 
                  { backgroundColor: item.color }
                ]}
                onPress={() => navigate(item.route)}
                activeOpacity={0.8}
              >
                <Text style={styles.menuIcon}>
                  {item.icon}
                </Text>
                <Text style={styles.menuLabel}>
                  {item.label}
                </Text>
              </TouchableOpacity>
            ))}
          </>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    margin: 0,
    padding: 0,
    overflow: "hidden",
  },
  content: {
    flex: 1,
    width: "100%",
    alignSelf: "center",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  contentMobile: {
    width: "100%",
    height: "100%",
    justifyContent: "space-around",
    alignItems: "center",
    padding: 0,
    paddingHorizontal: 20,
    margin: 0,
  },
  buildInfoBanner: {
    backgroundColor: "#E3F2EF",
    borderRadius: 6,
    padding: 12,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  buildInfoLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#5C6B68",
    marginBottom: 4,
  },
  buildInfoValue: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 2,
  },
  buildInfoVersion: {
    fontSize: 12,
    color: "#8A9794",
  },
  menuGrid: {
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    gap: 0,
    paddingHorizontal: 0,
    margin: 0,
  },
  menuGridDesktop: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    width: 360,
    gap: 20,
  },
  menuButton: {
    width: "100%",
    maxWidth: 400,
    height: 80,
    borderRadius: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingHorizontal: 24,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 5,
    margin: 0,
    marginVertical: 0,
  },
  menuButtonDesktop: {
    width: 160,
    height: 160,
    flexDirection: "column",
    justifyContent: "center",
    paddingHorizontal: 0,
    maxWidth: 160,
  },
  menuIcon: {
    fontSize: 36,
    marginRight: 16,
  },
  menuLabel: {
    fontSize: 20,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  menuIconDesktop: {
    fontSize: 64,
    marginRight: 0,
    marginBottom: 12,
  },
  menuLabelDesktop: {
    fontSize: 18,
    textAlign: "center",
  },
});

export default Dashboard;

